import { RefObject, useEffect, useState } from 'react';

interface UseFocusWithArrowKeysOptions {
  restoreLastFocusedElement?: boolean
}

export default function useFocusWithArrowKeys<T extends HTMLElement>(ref: RefObject<T>, {
  restoreLastFocusedElement,
} : UseFocusWithArrowKeysOptions = {}) {
  const [lastFocusedElement, setLastFocusedElement] = useState<HTMLElement>();

  const element = ref.current;

  useEffect(() => {
    if (!element) {
      return;
    }

    function handleFocusOut(e: FocusEvent) {
      setLastFocusedElement(e.target as HTMLElement);
    }

    function handleFocusIn(e: FocusEvent) {
      if (element.contains(e.relatedTarget as Element)) {
        return;
      }

      if (
        lastFocusedElement &&
        restoreLastFocusedElement
      ) {
        e.preventDefault();
        lastFocusedElement.focus();
        return;
      }

      const { first } = getFocusableElements(element);

      first?.focus();
    }

    function handleArrowKeys(key: string, preventDefault?: () => void) {
      const { focusableElements, first, last } = getFocusableElements(element);
      const activeElement = document.activeElement as HTMLElement;
      const currentIndex = focusableElements.indexOf(activeElement);

      let nextElementToFocus: HTMLElement | null = null;

      switch (key) {
        case 'ArrowDown':
        case 'ArrowRight':
          preventDefault();
          nextElementToFocus = focusableElements[currentIndex + 1] || first;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          preventDefault();
          nextElementToFocus = focusableElements[currentIndex - 1] || last;
          break;
        default:
          break;
      }

      // Focus on the next element if it exists
      if (nextElementToFocus) {
        nextElementToFocus.focus();
      }
    }

    function handleTab({
      shiftKey,
    }: {
      shiftKey?: boolean
    } = {}) {
      const { first, last } = getFocusableElements(element);
      const { focusableElements: focusableElementsInBody } = getFocusableElements(document.body);

      if (shiftKey) {
        const previousElement = focusableElementsInBody[focusableElementsInBody.indexOf(first) - 1];

        previousElement?.focus();

        return;
      }

      const next = focusableElementsInBody[focusableElementsInBody.indexOf(last) + 1];

      next?.focus();
    }

    function onKeyDown(e: KeyboardEvent) {
      const { key, shiftKey } = e;
      const isElementFocused = element.contains(document.activeElement);

      // Exit if the element does not have focus
      if (!isElementFocused) {
        return;
      }

      if (key === 'Tab') {
        e.preventDefault();
        handleTab({
          shiftKey,
        });
        return;
      }

      handleArrowKeys(key, () => e.preventDefault());
    }


    element.addEventListener('keydown', onKeyDown);
    element.addEventListener('focusout', handleFocusOut);
    element.addEventListener('focusin', handleFocusIn);

    // eslint-disable-next-line consistent-return
    return () => {
      element.removeEventListener('keydown', onKeyDown);
      element.removeEventListener('focusout', handleFocusOut);
      element.removeEventListener('focusin', handleFocusIn);
    };
  }, [lastFocusedElement, element, restoreLastFocusedElement]);
}

function getFocusableElements(element: Element) {
  const focusableElementTags = [
    'a[href]',
    'button',
    'input',
    'textarea',
    'select',
    'details',
    '[tabindex]:not([tabindex="-1"])',
  ];

  const focusableElements = [
    ...element.querySelectorAll(focusableElementTags.join(', ')),
  ].filter(
    (el) => !el.hasAttribute('disabled') && !el.hasAttribute('aria-hidden'),
  ) as HTMLElement[];

  return {
    focusableElements,
    first: focusableElements.length ? focusableElements[0] : null,
    last: focusableElements.length
      ? focusableElements[focusableElements.length - 1]
      : null,
  } as const;
}
